import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquareHeart, Send, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../api/client';
import { toast } from '../utils/toast';
import './FeedbackWidget.css';

const categories = [
  { value: 'general', label: 'General' },
  { value: 'bug', label: 'Problem' },
  { value: 'idea', label: 'Idea' },
  { value: 'praise', label: 'Working well' },
];

const ratings = [
  { value: 'good', label: 'Good' },
  { value: 'neutral', label: 'Okay' },
  { value: 'bad', label: 'Needs work' },
];

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export default function FeedbackWidget() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [rating, setRating] = useState('neutral');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messageRef = useRef(null);

  const pageTitle = useMemo(() => {
    const heading = document.querySelector('.page-title h2, h1');
    return heading?.textContent?.trim() || document.title || 'Acuon Platform';
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return undefined;
    messageRef.current?.focus();
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen]);

  const resetForm = () => {
    setMessage('');
    setCategory('general');
    setRating('neutral');
  };

  const submitFeedback = async (event) => {
    event.preventDefault();
    const cleanedMessage = message.trim();
    if (cleanedMessage.length < 3) {
      toast.warning('Please add a little more detail.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/user-feedback/submit`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          message: cleanedMessage,
          category,
          rating,
          page_path: `${location.pathname}${location.search}`,
          page_title: pageTitle,
          user_agent: window.navigator.userAgent,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || 'Feedback could not be sent');
      }

      resetForm();
      setIsOpen(false);
      toast.success('Feedback sent. Thank you.');
    } catch (error) {
      toast.error(error.message || 'Feedback could not be sent');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="feedback-trigger"
        onClick={() => setIsOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label="Send feedback"
        title="Send feedback"
      >
        <MessageSquareHeart size={18} />
      </button>

      {isOpen && createPortal(
        <div
          className="feedback-modal-layer"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsOpen(false);
          }}
        >
          <form
            aria-labelledby="feedback-dialog-title"
            aria-modal="true"
            className="feedback-panel"
            onSubmit={submitFeedback}
            role="dialog"
          >
          <div className="feedback-panel-header">
            <div>
              <div className="feedback-title" id="feedback-dialog-title">Send feedback</div>
              <div className="feedback-subtitle">Tell us what happened on this page.</div>
            </div>
            <button
              type="button"
              className="feedback-icon-button"
              onClick={() => setIsOpen(false)}
              aria-label="Close feedback form"
            >
              <X size={17} />
            </button>
          </div>

          <div className="feedback-field">
            <label htmlFor="feedback-category">Type</label>
            <select
              id="feedback-category"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              {categories.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>

          <div className="feedback-field">
            <span className="feedback-label">Experience</span>
            <div className="feedback-rating-group" role="group" aria-label="Experience rating">
              {ratings.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={rating === item.value ? 'selected' : ''}
                  onClick={() => setRating(item.value)}
                  aria-pressed={rating === item.value}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="feedback-field">
            <label htmlFor="feedback-message">Message</label>
            <textarea
              ref={messageRef}
              id="feedback-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="What should we fix, improve, or keep doing?"
            />
            <div className="feedback-count">{message.length}/2000</div>
          </div>

          <button type="submit" className="feedback-submit" disabled={isSubmitting || message.trim().length < 3}>
            <Send size={15} />
            {isSubmitting ? 'Sending' : 'Send feedback'}
          </button>
          </form>
        </div>,
        document.body,
      )}
    </>
  );
}
